import { useAuth } from "@clerk/tanstack-react-start";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { Button } from "~/components/ui/button";
import { CDNImage } from "~/components/ui/cdn-image";
import ImageLogoHorizontalUrl from "~/img/logo/dcm-horizontal-1.png";
import {
  getNextAvailableSummitOptions,
  getRecentlyEndedSummitOptions,
  checkUserSummitRegistrationOptions,
} from "~/lib/functions/summit-registration";
import { formatSummitDateRange } from "~/lib/summit";

import { cn } from "../../lib/cn";

export function SummitPromoCard({ className }: { className?: string }) {
  const { isSignedIn } = useAuth();
  const { data, isPending } = useQuery(getNextAvailableSummitOptions);
  const { data: recentlyEndedData, isPending: isRecentlyEndedPending } =
    useQuery({
      ...getRecentlyEndedSummitOptions,
      enabled: !data?.summit, // Only fetch if no active summit
    });
  const { data: registrationData } = useQuery({
    ...checkUserSummitRegistrationOptions,
    enabled: isSignedIn === true,
  });

  // Prefer active/upcoming summit, fall back to recently ended
  const activeSummit = data?.summit;
  const recentlyEndedSummit = recentlyEndedData?.summit;
  const isReplayMode = !activeSummit && !!recentlyEndedSummit;

  const summit = activeSummit ?? recentlyEndedSummit;
  const summitTitle = summit?.title;
  const summitDescription = summit?.description;
  const summitDateRange =
    summit?.startDate && summit?.endDate
      ? formatSummitDateRange(summit.startDate, summit.endDate)
      : null;

  const isRegistered = isSignedIn && registrationData?.isRegistered;

  // Determine link and CTA based on mode
  const linkTo = isReplayMode || isRegistered ? "/summit/agenda" : "/summit";
  const ctaText =
    isReplayMode || isRegistered ? "View recordings" : "Register for free";

  if (isPending || (!data?.summit && isRecentlyEndedPending)) {
    return null;
  }

  if (!summitTitle) {
    return null;
  }

  return (
    <Link
      to={linkTo}
      className={cn(
        "group relative block min-h-[26rem] w-full overflow-hidden rounded-xl bg-white/40 p-6 shadow-lg shadow-brand-800/10 backdrop-blur transition duration-300 hover:scale-[1.01] hover:shadow-xl hover:shadow-brand-800/20 active:scale-[0.99]",
        className,
      )}
    >
      <CDNImage
        className="absolute inset-0 z-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        src="https://form-data.dreamcoachmatch.com/summit/banner-bg.png"
        alt="Summit banner background"
        width={1200}
        height={675}
      />
      <div className="flex h-full flex-col gap-6 text-white md:justify-center">
        <div className="z-20 flex flex-col items-center gap-3 text-center md:hidden">
          <CDNImage
            src={ImageLogoHorizontalUrl}
            alt="Dream Coach Match logo"
            width={160}
            height={32}
            className="h-10 w-auto"
          />
          <h3 className="font-heading text-2xl leading-tight font-bold tracking-tight uppercase">
            {summitTitle}
          </h3>
          {summitDateRange ? (
            <span className="text-sm font-semibold tracking-wide text-white/80 uppercase">
              {summitDateRange}{" "}
              <span className="text-violet-400">| Online</span>
            </span>
          ) : null}
        </div>

        <div className="order-2 flex justify-center md:absolute md:inset-0 md:order-none md:flex md:items-center md:justify-start">
          <CDNImage
            className="z-20 w-full max-w-xs object-contain transition-transform duration-300 group-hover:scale-105 md:h-full md:w-[60%] md:max-w-none md:object-contain"
            src="https://form-data.dreamcoachmatch.com/summit/banner-fg.png"
            alt="Speakers at the summit"
            width={839}
            height={612}
          />
        </div>

        <div className="z-20 order-3 flex flex-col items-center gap-4 text-center md:absolute md:top-0 md:right-0 md:h-full md:w-1/2 md:justify-center md:gap-3 md:py-4 md:pr-4">
          <div className="hidden w-full flex-col items-center gap-3 text-center md:flex">
            <CDNImage
              src={ImageLogoHorizontalUrl}
              alt="Dream Coach Match logo"
              width={160}
              height={32}
            />
            <h3 className="font-heading text-2xl leading-tight font-bold tracking-tight uppercase">
              {summitTitle}
            </h3>
          </div>
          {summitDescription ? (
            <p className="max-w-xl text-sm font-bold text-white">
              {summitDescription}
            </p>
          ) : null}
          <p className="max-w-xl text-sm text-white">
            Online Conference With Keynotes and Live Workshops.
          </p>
          <Button
            size="sm"
            variant="primary"
            className="font-bold tracking-wide uppercase shadow-lg transition-transform duration-200 group-hover:scale-105 group-active:scale-95"
          >
            {ctaText}
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-medium text-white/70 md:text-xs">
              Supported by
            </span>
            <div className="flex items-center gap-6">
              <CDNImage
                src="https://form-data.dreamcoachmatch.com/public/Logo_ICF_neu.png"
                alt="International Coaching Federation logo"
                width={64}
                height={64}
                className="size-12"
              />
            </div>
          </div>
        </div>
      </div>
      {summitDateRange ? (
        <span className="pointer-events-none hidden text-sm font-semibold tracking-wide text-white/80 uppercase md:absolute md:bottom-8 md:left-10 md:z-20 md:block md:text-base">
          {summitDateRange} <span className="text-violet-400">| Online</span>
        </span>
      ) : null}
    </Link>
  );
}
